import { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Trash2,
  ExternalLink,
  Phone,
  Mail,
  User,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { VendorInvitation, PendingVendorStats } from '../types/vendorInvitation';
import { vendorInvitationService } from '../utils/vendors/invitationService';
import { formatDistanceToNow } from 'date-fns';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase/client';

interface PendingVendorsSectionProps {
  companyId: string;
  onVendorCompleted: () => void;
}

export default function PendingVendorsSection({
  companyId,
  onVendorCompleted,
}: PendingVendorsSectionProps) {
  const [invitations, setInvitations] = useState<VendorInvitation[]>([]);
  const [stats, setStats] = useState<PendingVendorStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'pending' | 'accepted' | 'completed'>('all');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  useEffect(() => {
    // Real-time listener - Firestore pushes updates, no polling needed
    // Use tenant-scoped path: companies/{companyId}/vendor_invitations
    const invitationsRef = collection(db, `companies/${companyId}/vendor_invitations`);
    let q = query(
      invitationsRef,
      orderBy('sentAt', 'desc')
    );

    // Add status filter if not 'all'
    if (selectedFilter === 'pending') {
      q = query(
        invitationsRef,
        where('status', 'in', ['sent', 'delivered']),
        orderBy('sentAt', 'desc')
      );
    } else if (selectedFilter !== 'all') {
      q = query(
        invitationsRef,
        where('status', '==', selectedFilter),
        orderBy('sentAt', 'desc')
      );
    }

    setIsLoading(true);

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamps to Dates
          sentAt: doc.data().sentAt?.toDate() || new Date(),
          expiresAt: doc.data().expiresAt?.toDate() || new Date(),
          respondedAt: doc.data().respondedAt?.toDate(),
          completedAt: doc.data().completedAt?.toDate(),
          cancelledAt: doc.data().cancelledAt?.toDate(),
        })) as VendorInvitation[];

        setInvitations(data);

        // Calculate stats from the data
        const allInvitations = data;
        setStats({
          total: allInvitations.length,
          sent: allInvitations.filter(i => ['sent', 'delivered'].includes(i.status)).length,
          accepted: allInvitations.filter(i => i.status === 'accepted').length,
          completed: allInvitations.filter(i => i.status === 'completed').length,
          expired: allInvitations.filter(i => i.status === 'expired').length,
          declined: allInvitations.filter(i => i.status === 'declined').length,
        });

        setIsLoading(false);
      },
      (error) => {
        console.error('Error listening to invitations:', error);
        toast.error('Failed to load invitations');
        setIsLoading(false);
      }
    );

    // Cleanup subscription on unmount or when filters change
    return () => unsubscribe();
  }, [companyId, selectedFilter]);

  const handleResend = async (invitation: VendorInvitation) => {
    setActionInProgress(invitation.id);

    try {
      const result = await vendorInvitationService.resendInvitation(invitation.id, companyId);

      if (result.success) {
        toast.success('Invitation resent successfully');
        // No need to reload - real-time listener will update automatically
      } else {
        toast.error(result.message || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('An error occurred');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCancel = async (invitation: VendorInvitation) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    setActionInProgress(invitation.id);

    try {
      const result = await vendorInvitationService.cancelInvitation(invitation.id, companyId);

      if (result.success) {
        toast.success('Invitation cancelled');
        // No need to reload - real-time listener will update automatically
      } else {
        toast.error(result.message || 'Failed to cancel invitation');
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('An error occurred');
    } finally {
      setActionInProgress(null);
    }
  };

  const getStatusBadge = (invitation: VendorInvitation) => {
    const statusConfig = {
      sent: { icon: Clock, color: 'bg-gray-100 text-gray-700', label: 'Sent' },
      delivered: { icon: CheckCircle, color: 'bg-blue-100 text-blue-700', label: 'Delivered' },
      accepted: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Accepted' },
      completed: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      declined: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Declined' },
      expired: { icon: AlertCircle, color: 'bg-orange-100 text-orange-700', label: 'Expired' },
      cancelled: { icon: XCircle, color: 'bg-gray-100 text-gray-700', label: 'Cancelled' },
    };

    const config = statusConfig[invitation.status];
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center space-x-1 px-3 py-1`}>
        <Icon className="h-3 w-3" />
        <span>{config.label}</span>
      </Badge>
    );
  };

  const getTimeDisplay = (invitation: VendorInvitation) => {
    if (invitation.status === 'completed' && invitation.completedAt) {
      return `Completed ${formatDistanceToNow(invitation.completedAt, { addSuffix: true })}`;
    }
    if (invitation.status === 'accepted' && invitation.respondedAt) {
      return `Accepted ${formatDistanceToNow(invitation.respondedAt, { addSuffix: true })}`;
    }
    if (invitation.status === 'declined' && invitation.respondedAt) {
      return `Declined ${formatDistanceToNow(invitation.respondedAt, { addSuffix: true })}`;
    }
    return `Sent ${formatDistanceToNow(invitation.sentAt, { addSuffix: true })}`;
  };

  const getExpirationDisplay = (invitation: VendorInvitation) => {
    if (['completed', 'declined', 'cancelled'].includes(invitation.status)) {
      return null;
    }

    const now = new Date();
    const hoursRemaining = Math.max(
      0,
      Math.floor((invitation.expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60))
    );

    if (invitation.status === 'expired' || hoursRemaining === 0) {
      return <span className="text-red-600 text-sm font-medium">Expired</span>;
    }

    const isUrgent = hoursRemaining < 6;

    return (
      <span className={`text-sm ${isUrgent ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
        Expires in {hoursRemaining}h
      </span>
    );
  };

  if (isLoading && !invitations.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600 mt-1">Total</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-200">
            <div className="text-2xl font-bold text-blue-900">{stats.sent}</div>
            <div className="text-sm text-blue-700 mt-1">Pending</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
            <div className="text-2xl font-bold text-green-900">{stats.accepted}</div>
            <div className="text-sm text-green-700 mt-1">Accepted</div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 shadow-sm border border-emerald-200">
            <div className="text-2xl font-bold text-emerald-900">{stats.completed}</div>
            <div className="text-sm text-emerald-700 mt-1">Completed</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 shadow-sm border border-orange-200">
            <div className="text-2xl font-bold text-orange-900">{stats.expired}</div>
            <div className="text-sm text-orange-700 mt-1">Expired</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 shadow-sm border border-red-200">
            <div className="text-2xl font-bold text-red-900">{stats.declined}</div>
            <div className="text-sm text-red-700 mt-1">Declined</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          {['all', 'pending', 'accepted', 'completed'].map((filter) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter as any)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${selectedFilter === filter
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        {/* Real-time indicator */}
        <div className="flex items-center space-x-2 text-sm text-green-600">
          <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
          <span>Live</span>
        </div>
      </div>

      {/* Invitations List */}
      {invitations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No invitations found</p>
          <p className="text-sm text-gray-500 mt-1">
            {selectedFilter === 'all'
              ? 'Send your first vendor invitation to get started'
              : `No ${selectedFilter} invitations`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {invitation.companyNameSuggestion || invitation.contactName || 'New Vendor'}
                      </h3>
                      {getStatusBadge(invitation)}
                    </div>
                    <p className="text-sm text-gray-600">{getTimeDisplay(invitation)}</p>
                  </div>

                  <div className="text-right">
                    {getExpirationDisplay(invitation)}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">{invitation.phoneNumber}</span>
                  </div>
                  {invitation.email && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{invitation.email}</span>
                    </div>
                  )}
                  {invitation.contactName && (
                    <div className="flex items-center space-x-2 text-sm">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{invitation.contactName}</span>
                    </div>
                  )}
                </div>

                {/* Invited By */}
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <span>Invited by</span>
                  <span className="font-medium text-gray-900">{invitation.invitedByName}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    {/* Resend Button */}
                    {['sent', 'delivered', 'expired'].includes(invitation.status) && (
                      <Button
                        onClick={() => handleResend(invitation)}
                        variant="outline"
                        size="sm"
                        disabled={actionInProgress === invitation.id}
                        className="flex items-center space-x-2"
                      >
                        {actionInProgress === invitation.id ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            <span>Resend</span>
                          </>
                        )}
                      </Button>
                    )}

                    {/* View Profile Button */}
                    {invitation.status === 'completed' && invitation.vendorId && (
                      <Button
                        onClick={() => {
                          // Navigate to vendor profile
                          toast.info('Opening vendor profile...');
                          onVendorCompleted();
                        }}
                        size="sm"
                        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span>View Profile</span>
                      </Button>
                    )}

                    {/* Copy Link Button */}
                    {['accepted', 'sent', 'delivered'].includes(invitation.status) && (
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(invitation.magicLinkUrl);
                          toast.success('Link copied to clipboard');
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center space-x-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span>Copy Link</span>
                      </Button>
                    )}
                  </div>

                  {/* Cancel Button */}
                  {!['completed', 'cancelled', 'declined'].includes(invitation.status) && (
                    <Button
                      onClick={() => handleCancel(invitation)}
                      variant="ghost"
                      size="sm"
                      disabled={actionInProgress === invitation.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>

                {/* SMS Attempts Info */}
                {invitation.smsAttempts > 1 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      SMS sent {invitation.smsAttempts} time{invitation.smsAttempts > 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
